/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Action, ActionCreators } from './actions';
import { Action as GlobalAction, ActionCreators as GlobalActionCreators } from '../actions';
import { SatelliteInFov } from '../../shared/entities/terminal';
import { ResourceFilter } from '../../components/common/table/types';
import { uniq } from 'lodash';
import { AnnotationPoint } from '../../components/fleet/FullTailView/charts/ChartWrappers/TimelineChartHelperFunctioners';
import Antenna from '../../shared/entities/antenna';
import MissionPlan from '../../shared/entities/missionPlan';
import FlightLeg from '../../shared/entities/flightLeg';
import { OverlaysContextsEnum, OverlaysContextsType } from '../maps/reducer';
import { PlatformListGroup } from '../../shared/entities/platformListGroup';

export enum SortDirection {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

export interface DetailsPanelGroupCollapseState {
  currentActivityGroupOpened: boolean;
  situationalCapabilitiesGroupOpened: boolean;
  aircraftStatusGroupOpened: boolean;
  tailConfigurationGroupOpened: boolean;
  networkingParametersGroupOpened: boolean;
  currentNetworkGroupOpened: boolean;
  terminalInformationGroupOpened: boolean;
  contactSummaryGroupOpened: boolean;
  activeLinksGroupOpened: boolean;
}

export interface HoveredPointPayload {
  annotation: Partial<AnnotationPoint> | null;
  source: 'flightpath' | 'timeline' | '';
}

export enum TerminalVisibilityToggleState {
  SHOW_ALL = 'SHOW_ALL',
  HIDE_ALL = 'HIDE_ALL',
}

export interface FleetViewMapCenterAndZoom {
  center: google.maps.LatLng;
  zoom: number;
}

export interface PlatformListSortDirectionPayload {
  groupName: PlatformListGroup;
  direction: SortDirection;
}

export interface OverlaysContextUiState {
  readonly platformListSortDirection: { [key in PlatformListGroup]: SortDirection };
  readonly platformListSortField: string;
  readonly platformListDrawerOpen: boolean;
  readonly collapsedTerminalsInPlatformList: { [datapathUuid: string]: boolean };
  readonly hiddenTerminalsList: string[];
  readonly terminalVisibilityToggleState: TerminalVisibilityToggleState;
  readonly selectedDatapathUuidInPlatformList: string | undefined;
  readonly selectedTerminalTabIndex?: number;
  readonly terminalFilters: ResourceFilter[];
  readonly detailsPanelOpen: boolean;
  readonly detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
}

export const defaultOverlaysContextUiState: OverlaysContextUiState = {
  platformListSortDirection: {
    [PlatformListGroup.TERMINAL]: SortDirection.ASCENDING,
    [PlatformListGroup.CONNECTED]: SortDirection.ASCENDING,
    [PlatformListGroup.DISCONNECTED]: SortDirection.ASCENDING,
    [PlatformListGroup.NETWORK]: SortDirection.ASCENDING,
  },
  platformListSortField: 'connectivityStatus',
  platformListDrawerOpen: true,
  collapsedTerminalsInPlatformList: {},
  hiddenTerminalsList: [],
  terminalVisibilityToggleState: TerminalVisibilityToggleState.SHOW_ALL,
  selectedDatapathUuidInPlatformList: undefined,
  selectedTerminalTabIndex: undefined,
  terminalFilters: [],
  detailsPanelOpen: false,
  detailsPanelGroupCollapseState: {
    currentActivityGroupOpened: true,
    situationalCapabilitiesGroupOpened: true,
    aircraftStatusGroupOpened: true,
    tailConfigurationGroupOpened: true,
    networkingParametersGroupOpened: true,
    currentNetworkGroupOpened: true,
    terminalInformationGroupOpened: true,
    contactSummaryGroupOpened: true,
    activeLinksGroupOpened: true,
  },
};
export interface State {
  readonly activeFlightPath: [] | undefined;
  readonly hoveredAlternativeSatelliteInFov?: SatelliteInFov;
  readonly alternativeSatelliteInFovCollapseOpen: boolean;
  readonly terminalsInYourBeamPanelShown: boolean;
  readonly hiddenTerminalsInYourBeamDatapathUuids: string[];
  readonly visibleTerminalsInYourBeamListSortDirection: SortDirection;
  readonly visibleTerminalsInYourBeamListSortField: string;

  readonly printMode: boolean;
  fleetViewAntenna: Antenna | undefined;
  // FTV settings
  readonly hoveredAnnotation: HoveredPointPayload;
  readonly tooltipStepMap: { [key: number]: number };

  readonly hoveredBeamId: string | null;
  readonly allProtocolsModalVisible: boolean;

  // Mission Overlay
  readonly overlayMissionModalVisible: boolean;
  readonly overlayMissionMissionListVisible: boolean;
  readonly overlayMissionFlightLegListVisible: boolean;

  readonly ftvTerminalsInYourBeamCollapsedTerminals: { [datapathUuid: string]: boolean };

  readonly fleetViewMapCenterAndZoom?: FleetViewMapCenterAndZoom;

  readonly ftvShouldZoomToFlightPath: boolean;

  // FTV Mission Overlay Table
  readonly missionOverlayMissionsSortField: keyof MissionPlan;
  readonly missionOverlayMissionsSortDirection: 'asc' | 'desc';
  readonly missionOverlayFlightLegsSortField: keyof FlightLeg;
  readonly missionOverlayFlightLegsSortDirection: 'asc' | 'desc';

  // Export
  readonly exportViewTitle: string | undefined;

  // Overlays Context UI State
  readonly overlaysCurrentContext: OverlaysContextsEnum;
  readonly overlaysContextUiState: { [key in OverlaysContextsType]: OverlaysContextUiState };
}

export const initialUiState: State = {
  activeFlightPath: undefined,
  fleetViewAntenna: undefined,
  hoveredAlternativeSatelliteInFov: undefined,
  alternativeSatelliteInFovCollapseOpen: false,

  // Tails in your Beam Panel
  terminalsInYourBeamPanelShown: false,
  hiddenTerminalsInYourBeamDatapathUuids: [],
  visibleTerminalsInYourBeamListSortDirection: SortDirection.ASCENDING,
  visibleTerminalsInYourBeamListSortField: 'connectivityStatus',

  printMode: false,

  // FTV Settings
  hoveredAnnotation: { annotation: null, source: '' },
  tooltipStepMap: {},

  hoveredBeamId: null,
  allProtocolsModalVisible: false,
  overlayMissionModalVisible: false,
  overlayMissionMissionListVisible: false,
  overlayMissionFlightLegListVisible: false,

  ftvTerminalsInYourBeamCollapsedTerminals: {},
  fleetViewMapCenterAndZoom: undefined,
  ftvShouldZoomToFlightPath: true,

  // FTV Mission Ovelay Table
  missionOverlayMissionsSortField: 'creationDate',
  missionOverlayMissionsSortDirection: 'desc',
  missionOverlayFlightLegsSortField: 'legOrder',
  missionOverlayFlightLegsSortDirection: 'desc',

  //export
  exportViewTitle: undefined,

  // Overlays Context UI State
  overlaysCurrentContext: OverlaysContextsEnum.FLEET_VIEW,
  overlaysContextUiState: {
    [OverlaysContextsEnum.FLEET_VIEW]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.FTV]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.FTV_EXPORT]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.PERFORMANCE_OUTLOOK]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.PERFORMANCE_OUTLOOK_EXPORT]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.TRANSPORT_MONITORING]: {
      ...defaultOverlaysContextUiState,
    },
    [OverlaysContextsEnum.FULL_SPACE_VEHICLE_VIEW]: {
      ...defaultOverlaysContextUiState,
    },
  },
};

// Redux Reducer
export default function reducer(state: State = initialUiState, action: Action | GlobalAction): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case GlobalActionCreators.SwitchOverlaysContext.type: {
      return {
        ...state,
        overlaysCurrentContext: action.payload,
      };
    }
    case ActionCreators.ShowTerminalsInYourBeamPanel.type:
      partialState = {
        terminalsInYourBeamPanelShown: true,
      };
      break;
    case ActionCreators.HideTerminalsInYourBeamPanel.type:
      partialState = {
        terminalsInYourBeamPanelShown: false,
      };
      break;
    case ActionCreators.ToggleTerminalsInYourBeamPanel.type:
      partialState = {
        terminalsInYourBeamPanelShown: !state.terminalsInYourBeamPanelShown,
      };
      break;
    case ActionCreators.ShowTerminalInYourBeam.type: {
      const hiddenTerminalsInYourBeamDatapathUuids = state.hiddenTerminalsInYourBeamDatapathUuids.filter(
        (datapathUuid: string) => datapathUuid !== action.payload,
      );
      partialState = {
        hiddenTerminalsInYourBeamDatapathUuids,
      };
      break;
    }
    case ActionCreators.HideTerminalInYourBeam.type: {
      // remove it if it already exists or not
      const hiddenTerminalsInYourBeamDatapathUuids = state.hiddenTerminalsInYourBeamDatapathUuids.slice();

      if (!state.hiddenTerminalsInYourBeamDatapathUuids.includes(action.payload)) {
        // then add it or re add it to prevent duplicates
        hiddenTerminalsInYourBeamDatapathUuids.push(action.payload);
      }
      partialState = {
        hiddenTerminalsInYourBeamDatapathUuids,
      };
      break;
    }
    case ActionCreators.CollapseTerminalInBeamListItem.type: {
      partialState = {
        ftvTerminalsInYourBeamCollapsedTerminals: {
          ...state.ftvTerminalsInYourBeamCollapsedTerminals,
          [action.payload]: true,
        },
      };
      break;
    }
    case ActionCreators.ExpandTerminalInBeamListItem.type: {
      partialState = {
        ftvTerminalsInYourBeamCollapsedTerminals: {
          ...state.ftvTerminalsInYourBeamCollapsedTerminals,
          [action.payload]: false,
        },
      };
      break;
    }
    case ActionCreators.SetFtvTerminalsInYourBeamCollapsedTerminals.type: {
      partialState = {
        ftvTerminalsInYourBeamCollapsedTerminals: { ...action.payload },
      };
      break;
    }
    case ActionCreators.HideTerminalsInYourBeam.type: {
      partialState = {
        hiddenTerminalsInYourBeamDatapathUuids: uniq([...state.hiddenTerminalsInYourBeamDatapathUuids, ...action.payload]),
      };
      break;
    }
    case ActionCreators.ShowAllTerminalsInYourBeam.type: {
      return {
        ...state,
        hiddenTerminalsInYourBeamDatapathUuids: [],
      };
    }
    case ActionCreators.SetTerminalsInYourBeamListSortField.type:
      partialState = {
        visibleTerminalsInYourBeamListSortField: action.payload,
      };
      break;
    case ActionCreators.SetTerminalsYourBeamListSortDirection.type:
      partialState = {
        visibleTerminalsInYourBeamListSortDirection: action.payload,
      };
      break;

    case ActionCreators.OpenDetailsPanel.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            detailsPanelOpen: true,
          },
        },
      };
      break;

    case ActionCreators.CloseDetailsPanel.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            detailsPanelOpen: false,
            detailsPanelGroupCollapseState: {
              currentActivityGroupOpened: true,
              situationalCapabilitiesGroupOpened: true,
              aircraftStatusGroupOpened: true,
              tailConfigurationGroupOpened: true,
              networkingParametersGroupOpened: true,
              currentNetworkGroupOpened: true,
              terminalInformationGroupOpened: true,
              contactSummaryGroupOpened: true,
              activeLinksGroupOpened: true,
            },
            selectedDatapathUuidInPlatformList: undefined,
            selectedTerminalTabIndex: undefined,
          },
        },
      };
      break;

    case ActionCreators.SetDetailsPanelGroupCollapseState.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            detailsPanelGroupCollapseState: action.payload,
          },
        },
      };
      break;

    case ActionCreators.setHoveredAlternativeSatelliteInFov.type:
      partialState = {
        hoveredAlternativeSatelliteInFov: action.payload,
      };
      break;

    case ActionCreators.collapseAlternativeSatellitesInFov.type:
      partialState = {
        alternativeSatelliteInFovCollapseOpen: false,
      };
      break;

    case ActionCreators.expandAlternativeSatellitesInFov.type:
      partialState = {
        alternativeSatelliteInFovCollapseOpen: true,
      };
      break;

    case ActionCreators.SetFleetViewAntenna.type: {
      const antenna = action.payload;
      return {
        ...state,
        fleetViewAntenna: antenna,
      };
    }

    case ActionCreators.SetHoveredBeam.type: {
      partialState = {
        hoveredBeamId: action.payload,
      };
      break;
    }

    case ActionCreators.ShowMissionOverlayMissionListModal.type: {
      partialState = {
        overlayMissionModalVisible: true,
      };
      break;
    }
    case ActionCreators.HideMissionOverlayMissionListModal.type: {
      partialState = {
        overlayMissionModalVisible: false,
      };
      break;
    }
    case ActionCreators.ShowMissionOverlayMissionListInModal.type: {
      partialState = {
        overlayMissionMissionListVisible: true,
      };
      break;
    }
    case ActionCreators.HideMissionOverlayMissionListInModal.type: {
      partialState = {
        overlayMissionMissionListVisible: false,
      };
      break;
    }
    case ActionCreators.ShowMissionOverlayFlightLegListInModal.type: {
      partialState = {
        overlayMissionFlightLegListVisible: true,
      };
      break;
    }
    case ActionCreators.HideMissionOverlayFlightLegListInModal.type: {
      partialState = {
        overlayMissionFlightLegListVisible: false,
      };
      break;
    }

    case ActionCreators.SetPrintMode.type: {
      return {
        ...state,
        printMode: true,
      };
    }

    case ActionCreators.UnsetPrintMode.type: {
      return {
        ...state,
        printMode: false,
      };
    }
    case ActionCreators.SetPrintModeAndPrint.type: {
      return {
        ...state,
        printMode: true,
      };
    }

    case ActionCreators.SetHoveredAnnotation.type: {
      return {
        ...state,
        hoveredAnnotation: {
          annotation: action.payload.annotation,
          source: action.payload.source,
        },
      };
    }

    case ActionCreators.SetFleetViewMapCenterAndZoom.type:
      partialState = {
        fleetViewMapCenterAndZoom: action.payload,
      };
      break;

    case ActionCreators.SetFTVShouldZoomToFlightPath.type:
      partialState = {
        ftvShouldZoomToFlightPath: action.payload,
      };
      break;

    case ActionCreators.SetTooltipStepMap.type:
      partialState = {
        tooltipStepMap: action.payload,
      };
      break;

    case ActionCreators.SetMissionOverlayMissionsSortField.type:
      partialState = {
        missionOverlayMissionsSortField: action.payload,
      };
      break;
    case ActionCreators.SetMissionOverlayMissionsSortDirection.type:
      partialState = {
        missionOverlayMissionsSortDirection: action.payload,
      };
      break;
    case ActionCreators.SetMissionOverlayFlightLegsSortField.type:
      partialState = {
        missionOverlayFlightLegsSortField: action.payload,
      };
      break;
    case ActionCreators.SetMissionOverlayFlightLegsSortDirection.type:
      partialState = {
        missionOverlayFlightLegsSortDirection: action.payload,
      };
      break;
    case ActionCreators.SetExportViewTitle.type: {
      return {
        ...state,
        exportViewTitle: action.payload,
      };
    }
    case ActionCreators.ClearExportViewTitle.type: {
      return {
        ...state,
        exportViewTitle: undefined,
      };
    }

    /*
     * Overlays Context UI State
     */
    case ActionCreators.OpenPlatformListDrawer.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            platformListDrawerOpen: true,
          },
        },
      };
      break;

    case ActionCreators.ClosePlatformListDrawer.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            platformListDrawerOpen: false,
          },
        },
      };
      break;

    case ActionCreators.SetPlatformListSortField.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            platformListSortField: action.payload,
          },
        },
      };
      break;

    case ActionCreators.SetPlatformListSortDirection.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            platformListSortDirection: {
              ...state.overlaysContextUiState[state.overlaysCurrentContext].platformListSortDirection,
              [action.payload.groupName]: action.payload.direction,
            },
          },
        },
      };
      break;

    case ActionCreators.SetCollapsedTerminalsInPlatformList.type: {
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            collapsedTerminalsInPlatformList: { ...action.payload },
          },
        },
      };
      break;
    }

    case ActionCreators.CollapseTerminalInPlatformList.type: {
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            collapsedTerminalsInPlatformList: {
              ...state.overlaysContextUiState[state.overlaysCurrentContext].collapsedTerminalsInPlatformList,
              [action.payload]: true,
            },
          },
        },
      };
      break;
    }

    case ActionCreators.ExpandTerminalInPlatformList.type: {
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            collapsedTerminalsInPlatformList: {
              ...state.overlaysContextUiState[state.overlaysCurrentContext].collapsedTerminalsInPlatformList,
              [action.payload]: false,
            },
          },
        },
      };
      break;
    }

    case ActionCreators.ShowTerminalInPlatformList.type: {
      const hiddenTerminals = state.overlaysContextUiState[state.overlaysCurrentContext].hiddenTerminalsList.filter(
        (datapathUuid: string) => datapathUuid !== action.payload,
      );
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: hiddenTerminals,
          },
        },
      };
      break;
    }

    case ActionCreators.ShowTerminalsInPlatformList.type: {
      const hiddenTerminals = state.overlaysContextUiState[state.overlaysCurrentContext].hiddenTerminalsList.filter(
        (datapathUuid: string) => !action.payload.includes(datapathUuid),
      );
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: hiddenTerminals,
          },
        },
      };
      break;
    }

    case ActionCreators.HideTerminalInPlatformList.type: {
      const hiddenTerminals = state.overlaysContextUiState[state.overlaysCurrentContext].hiddenTerminalsList.slice();

      if (!state.overlaysContextUiState[state.overlaysCurrentContext].hiddenTerminalsList.includes(action.payload)) {
        // then add it or re add it to prevent duplicates
        hiddenTerminals.push(action.payload);
      }
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: hiddenTerminals,
          },
        },
      };
      break;
    }

    case ActionCreators.HideTerminalsInPlatformList.type: {
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: uniq([
              ...state.overlaysContextUiState[state.overlaysCurrentContext].hiddenTerminalsList,
              ...action.payload,
            ]),
          },
        },
      };
      break;
    }

    case ActionCreators.HideAllTerminalsInPlatformList.type: {
      return {
        ...state,
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: action.payload.slice(),
            terminalVisibilityToggleState: TerminalVisibilityToggleState.HIDE_ALL,
          },
        },
      };
    }

    case ActionCreators.ShowAllTerminalsInPlatformList.type: {
      return {
        ...state,
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            hiddenTerminalsList: [],
            terminalVisibilityToggleState: TerminalVisibilityToggleState.SHOW_ALL,
          },
        },
      };
    }

    case ActionCreators.SetSelectedDatapathUuidInPlatformList.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            selectedDatapathUuidInPlatformList: action.payload,
          },
        },
      };
      break;

    case ActionCreators.ClearSelectedDatapathUuidInPlatformList.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            selectedDatapathUuidInPlatformList: undefined,
          },
        },
      };
      break;

    case ActionCreators.SetSelectedTerminalTabIndex.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            selectedTerminalTabIndex: action.payload,
          },
        },
      };
      break;

    case ActionCreators.ClearSelectedTerminalTabIndex.type:
      partialState = {
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            selectedTerminalTabIndex: undefined,
          },
        },
      };
      break;

    case ActionCreators.SetTerminalFilters.type: {
      return {
        ...state,
        overlaysContextUiState: {
          ...state.overlaysContextUiState,
          [state.overlaysCurrentContext]: {
            ...state.overlaysContextUiState[state.overlaysCurrentContext],
            terminalFilters: action.payload,
          },
        },
      };
    }

    default:
      return state;
  }

  return { ...state, ...partialState };
}
